import { TranslocoPipe } from '@jsverse/transloco';
import { Component, DestroyRef, HostListener, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import {
  LucideBarChart3,
  LucideBell,
  LucideCalendarDays,
  LucideChevronDown,
  LucideHouse,
  LucidePanelLeftClose,
  LucidePanelLeftOpen,
  LucideRadio,
  LucideSettings,
  LucideTrophy,
  LucideUserRoundCheck,
  LucideUsers,
} from '@lucide/angular';
import { AppStateService } from '../../../core/app-state/app-state.service';
import { HapticsService } from '../../../core/platform/haptics.service';
import { AppLogoComponent } from '../app-logo/app-logo.component';
import { PlanBadgeComponent } from '../plan-badge/plan-badge.component';
import { UpgradeSheetComponent } from '../upgrade-sheet/upgrade-sheet.component';
import { AppTabBarComponent } from '../app-tab-bar/app-tab-bar.component';
import { AppSheetComponent } from '../app-sheet/app-sheet.component';
import { NotificationListComponent } from '../notification-list/notification-list.component';
import { SwipeBackDirective } from '../../../shared/directives/swipe-back.directive';

/**
 * App chrome. Desktop keeps a persistent, collapsible left sidebar with the full navigation.
 * Mobile drops the sidebar for a persistent bottom tab bar (Home/Matches/Stats/Community) plus a "More"
 * bottom sheet holding the secondary destinations, notifications and settings — the
 * native pattern, no top header and no hamburger drawer. Sign-out lives only in Settings.
 */
@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [TranslocoPipe, RouterOutlet,
    RouterLink,
    RouterLinkActive,
    LucideHouse,
    LucideTrophy,
    LucideBarChart3,
    LucideUsers,
    LucideCalendarDays,
    LucideUserRoundCheck,
    LucideSettings,
    LucideBell,
    LucideChevronDown,
    LucidePanelLeftClose,
    LucidePanelLeftOpen,
    LucideRadio,
    AppLogoComponent,
    PlanBadgeComponent,
    UpgradeSheetComponent,
    AppTabBarComponent,
    AppSheetComponent,
    NotificationListComponent,
    SwipeBackDirective,
  ],
  template: `
    <div
      class="shell"
      [class.shell--collapsed]="collapsed()"
      [class.shell--focus]="isFocusRoute()"
    >
      <aside class="side-nav" [class.side-nav--collapsed]="collapsed()">
        <div class="side-nav__top">
          <div class="brand-group">
            <a class="brand" routerLink="/app/home" [attr.aria-label]="'core.home' | transloco">
              <app-logo />
            </a>
            <app-plan-badge class="desktop-pro-badge" [compact]="true" />
          </div>
          <button class="icon-button desktop-only" type="button" (click)="collapsed.set(!collapsed())" [attr.aria-label]="(collapsed() ? 'core.expandSidebar' : 'core.collapseSidebar') | transloco">
            @if (collapsed()) {
              <svg lucidePanelLeftOpen size="18"></svg>
            } @else {
              <svg lucidePanelLeftClose size="18"></svg>
            }
          </button>
        </div>

        <nav [attr.aria-label]="'core.primaryNavigation' | transloco">
          <a routerLink="/app/home" routerLinkActive="active" [attr.aria-label]="'core.home' | transloco" [title]="'core.home' | transloco">
            <svg lucideHouse size="18"></svg>
            <span>{{ 'core.home' | transloco }}</span>
          </a>
          <a routerLink="/app/community" [class.active]="communityActive()" [attr.aria-label]="'core.community' | transloco" [title]="'core.community' | transloco">
            <svg lucideCalendarDays size="18"></svg>
            <span>{{ 'core.community' | transloco }}</span>
          </a>
          <a routerLink="/app/matches" routerLinkActive="active" [attr.aria-label]="'core.matches' | transloco" [title]="'core.matches' | transloco">
            <svg lucideTrophy size="18"></svg>
            <span>{{ 'core.matches' | transloco }}</span>
          </a>
          <a routerLink="/app/stream" routerLinkActive="active" [attr.aria-label]="'core.stream' | transloco" [title]="'core.stream' | transloco">
            <svg lucideRadio size="18"></svg>
            <span>{{ 'core.stream' | transloco }}</span>
          </a>
          <a routerLink="/app/stats" routerLinkActive="active" [attr.aria-label]="'core.stats' | transloco" [title]="'core.stats' | transloco">
            <svg lucideBarChart3 size="18"></svg>
            <span>{{ 'core.stats' | transloco }}</span>
          </a>
          <a routerLink="/app/players" routerLinkActive="active" [attr.aria-label]="'core.players' | transloco" [title]="'core.players' | transloco">
            <svg lucideUsers size="18"></svg>
            <span>{{ 'core.players' | transloco }}</span>
          </a>
          <a routerLink="/app/playzone" routerLinkActive="active" [attr.aria-label]="'core.playzone' | transloco" [title]="'core.playzone' | transloco">
            <svg lucideUserRoundCheck size="18"></svg>
            <span>{{ 'core.playzone' | transloco }}</span>
          </a>
        </nav>

        <div class="utility">
          <button
            type="button"
            class="util-row"
            [class.util-row--unread]="notificationCount() > 0"
            [attr.aria-expanded]="notifOpen()"
            [attr.aria-label]="'core.notifications' | transloco"
            (click)="toggleNotifications()"
          >
            <span class="util-row__icon">
              <svg lucideBell size="18"></svg>
              @if (notificationCount() > 0) {
                <span class="util-badge" aria-hidden="true">{{ notificationCount() > 9 ? '9+' : notificationCount() }}</span>
              }
            </span>
            <span class="util-row__label">{{ 'core.notifications' | transloco }}</span>
            <svg class="util-row__chev" [class.open]="notifOpen()" lucideChevronDown size="16"></svg>
          </button>

          @if (notifOpen() && !collapsed()) {
            <app-notification-list />
          }

          <a class="util-row" routerLink="/app/settings" routerLinkActive="active" [attr.aria-label]="'core.settings' | transloco" [title]="'core.settings' | transloco">
            <span class="util-row__icon"><svg lucideSettings size="18"></svg></span>
            <span class="util-row__label">{{ 'core.settings' | transloco }}</span>
          </a>
        </div>

        <div class="side-nav__footer">
          <div class="user-chip">
            <small>{{ initials() }}</small>
            <span>{{ state.currentUser()?.displayName }}</span>
          </div>
        </div>
      </aside>

      <div class="workspace" appSwipeBack [appSwipeBackDisabled]="isLiveMatchRoute()">
        <main>
          <router-outlet />
        </main>
      </div>

      <app-tab-bar
        [communityActive]="communityActive()"
        [moreActive]="moreOpen()"
        [notificationCount]="notificationCount()"
        [homeLabel]="'core.home' | transloco"
        [matchesLabel]="'core.matches' | transloco"
        [statsLabel]="'core.stats' | transloco"
        (tabSelect)="haptics.selection()"
        (moreClick)="openMore()"
      />

      <app-sheet anchor="bottom" [sheetTitle]="'core.more' | transloco" [open]="moreOpen()" (dismissed)="closeMore()">
        <nav class="more-nav" [attr.aria-label]="'core.moreNavigation' | transloco">
          <a routerLink="/app/players" (click)="closeMore()" [attr.aria-label]="'core.players' | transloco">
            <svg lucideUsers size="20"></svg>
            <span>{{ 'core.players' | transloco }}</span>
          </a>
          <a routerLink="/app/playzone" (click)="closeMore()" [attr.aria-label]="'core.playzone' | transloco">
            <svg lucideUserRoundCheck size="20"></svg>
            <span>{{ 'core.playzone' | transloco }}</span>
          </a>
          <a routerLink="/app/stream" (click)="closeMore()" [attr.aria-label]="'core.stream' | transloco">
            <svg lucideRadio size="20"></svg>
            <span>{{ 'core.stream' | transloco }}</span>
          </a>
          <a routerLink="/app/settings" (click)="closeMore()" [attr.aria-label]="'core.settings' | transloco">
            <svg lucideSettings size="20"></svg>
            <span>{{ 'core.settings' | transloco }}</span>
          </a>
        </nav>

        <section class="more-notifs">
          <h3>
            {{ 'core.notifications' | transloco }}
            @if (notificationCount() > 0) {
              <span class="more-count" aria-hidden="true">{{ notificationCount() }}</span>
            }
          </h3>
          <app-notification-list (navigated)="closeMore()" />
        </section>

        <div class="more-user">
          <div class="user-chip">
            <small>{{ initials() }}</small>
            <span>{{ state.currentUser()?.displayName }}</span>
          </div>
        </div>
      </app-sheet>
      <app-upgrade-sheet />
    </div>
  `,
  styles: [`
    .shell {
      --sidebar-width: 16rem;
      display: grid;
      grid-template-columns: var(--sidebar-width) minmax(0, 1fr);
      height: 100vh;
      overflow: hidden;
      transition: grid-template-columns var(--motion-medium);
    }

    .shell--collapsed {
      --sidebar-width: 5rem;
    }

    .side-nav {
      background: color-mix(in srgb, var(--color-surface) 92%, var(--color-bg));
      border-right: 1px solid var(--color-border-subtle);
      display: flex;
      flex-direction: column;
      gap: var(--space-5);
      height: 100vh;
      overflow-y: auto;
      padding: var(--space-5);
      z-index: 12;
    }

    .side-nav__top,
    .brand-group,
    .brand,
    nav a,
    .user-chip {
      align-items: center;
      display: flex;
      min-width: 0;
    }

    .side-nav__top {
      gap: var(--space-3);
      justify-content: space-between;
    }

    .brand-group {
      gap: var(--space-2);
      min-width: 0;
    }

    .brand {
      gap: var(--space-3);
      overflow: hidden;
    }

    .icon-button {
      align-items: center;
      background: var(--color-surface);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-md);
      color: var(--color-text-secondary);
      display: flex;
      flex: 0 0 auto;
      height: 2.25rem;
      justify-content: center;
      width: 2.25rem;
    }

    nav {
      display: grid;
      gap: var(--space-2);
    }

    nav a,
    .util-row {
      border-radius: var(--radius-md);
      color: var(--color-text-secondary);
      gap: var(--space-3);
      font-weight: 900;
      min-height: 3.05rem;
      padding: 0 var(--space-3);
    }

    nav a svg {
      flex: 0 0 auto;
    }

    nav a span,
    .util-row__label,
    .user-chip span {
      transition: opacity var(--motion-fast);
    }

    nav a.active,
    nav a:hover,
    .util-row:hover,
    .util-row.active {
      background: color-mix(in srgb, var(--color-action) 10%, var(--color-surface));
      color: var(--color-primary);
    }

    .utility {
      border-top: 1px solid var(--color-border-subtle);
      display: grid;
      gap: var(--space-2);
      margin-top: auto;
      padding-top: var(--space-4);
    }

    .util-row {
      align-items: center;
      background: none;
      border: 0;
      cursor: pointer;
      display: flex;
      text-align: left;
      width: 100%;
    }

    .util-row__icon {
      align-items: center;
      display: inline-flex;
      flex: 0 0 auto;
      justify-content: center;
      position: relative;
    }

    .util-row__label { flex: 1 1 auto; min-width: 0; }
    .util-row__chev { flex: 0 0 auto; transition: transform var(--motion-fast); }
    .util-row__chev.open { transform: rotate(180deg); }
    .util-row--unread { color: var(--color-text-primary); }

    .util-badge {
      align-items: center;
      animation: pop var(--motion-medium) var(--ease-spring);
      background: var(--color-danger);
      border-radius: var(--radius-pill);
      color: var(--color-on-strong);
      display: inline-flex;
      font-size: 0.62rem;
      font-weight: 900;
      justify-content: center;
      line-height: 1;
      min-width: 1.05rem;
      padding: 0.18rem 0.3rem;
      position: absolute;
      right: -0.5rem;
      top: -0.4rem;
    }

    .side-nav__footer {
      border-top: 1px solid var(--color-border-subtle);
      display: grid;
      gap: var(--space-3);
      padding-top: var(--space-4);
    }

    .user-chip {
      gap: var(--space-3);
      overflow: hidden;
    }

    .user-chip small {
      align-items: center;
      background: var(--color-surface-high);
      border: 1px solid var(--color-border-subtle);
      border-radius: 50%;
      display: grid;
      flex: 0 0 auto;
      font-size: var(--font-size-caption);
      font-weight: 900;
      height: 2.1rem;
      justify-items: center;
      width: 2.1rem;
    }

    .user-chip span {
      color: var(--color-text-secondary);
      font-size: 0.86rem;
      font-weight: 800;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .workspace {
      /* How far bottom-docked page UI (app-action-bar) must sit above the viewport edge. */
      --app-bottom-inset: env(safe-area-inset-bottom, 0px);
      height: 100vh;
      min-width: 0;
      overflow-y: auto;
      overscroll-behavior-y: contain;
      padding: var(--page-padding-desktop);
    }

    main {
      position: relative;
    }

    /* Route changes run as a router View Transition (shared/animations/route-transition.ts). Naming
       main only while one is in flight lifts the page out of the root snapshot, so the sidebar and
       tab bar stay put while the content slides; in-page View Transitions leave main unnamed.
       A named snapshot paints above the root one, so the chrome that overlaps main (sidebar, fixed
       tab bar) is named too: its own snapshots keep their usual stacking above the sliding page. */
    :host-context(:root[data-nav-direction]) main {
      view-transition-name: route-main;
    }
    :host-context(:root[data-nav-direction]) .side-nav {
      view-transition-name: route-side-nav;
    }
    :host-context(:root[data-nav-direction]) app-tab-bar {
      view-transition-name: route-tab-bar;
    }

    /* Secondary nav inside the mobile "More" sheet. nav a styling is inherited from the rules above
       (this content is projected from the shell's view), so only sheet-specific bits live here. */
    .more-nav { margin-top: var(--space-1); }
    .more-notifs { display: grid; gap: var(--space-2); }
    .more-notifs h3 {
      align-items: center;
      color: var(--color-text-secondary);
      display: flex;
      font-size: var(--font-size-label);
      font-weight: 900;
      gap: var(--space-2);
      letter-spacing: 0.02em;
      margin: 0;
      text-transform: uppercase;
    }
    .more-count {
      background: var(--color-danger);
      border-radius: var(--radius-pill);
      color: var(--color-on-strong);
      font-size: 0.6rem;
      padding: 0.1rem 0.4rem;
    }
    .more-user {
      align-items: center;
      border-top: 1px solid var(--color-border-subtle);
      display: flex;
      gap: var(--space-3);
      padding-top: var(--space-3);
    }

    .side-nav--collapsed {
      padding-inline: 0.75rem;
    }

    .side-nav--collapsed .side-nav__top,
    .side-nav--collapsed .side-nav__footer {
      justify-items: center;
    }

    .side-nav--collapsed .brand,
    .side-nav--collapsed .desktop-pro-badge {
      display: none;
    }

    .side-nav--collapsed nav a span,
    .side-nav--collapsed .util-row__label,
    .side-nav--collapsed .util-row__chev,
    .side-nav--collapsed .user-chip span {
      opacity: 0;
      position: absolute;
      pointer-events: none;
    }

    .side-nav--collapsed nav a,
    .side-nav--collapsed .util-row {
      justify-content: center;
      padding-inline: 0;
    }

    .side-nav--collapsed .user-chip {
      justify-content: center;
    }

    @media (max-width: 1023px) {
      .shell {
        display: block;
        height: auto;
        overflow: visible;
      }

      /* The sidebar gives way to the bottom tab bar + More sheet on mobile. */
      .side-nav { display: none; }

      .workspace {
        height: auto;
        overflow-y: visible;
        padding: var(--page-padding-mobile);
        /* Clear the fixed tab bar (3.5rem) plus the home-indicator inset. */
        --app-bottom-inset: calc(3.5rem + env(safe-area-inset-bottom, 0px));
        padding-bottom: calc(var(--app-bottom-inset) + var(--space-4));
      }

      /* No bottom bar competes with the live scoreboard or the pickup action bar. */
      .shell--focus app-tab-bar { display: none; }
      .shell--focus .workspace {
        --app-bottom-inset: env(safe-area-inset-bottom, 0px);
        padding-bottom: var(--page-padding-mobile);
      }
    }
  `],
})
export class AppShellComponent {
  readonly state = inject(AppStateService);
  readonly haptics = inject(HapticsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly collapsed = signal(false);
  readonly moreOpen = signal(false);
  readonly notifOpen = signal(false);
  readonly currentUrl = signal(this.router.url);
  readonly isLiveMatchRoute = computed(() => /^\/app\/matches\/[^/]+\/live(?:[?#].*)?$/.test(this.currentUrl()));
  /** Focus routes own the bottom edge (scoreboard, pickup action bar), so the tab bar steps aside. */
  readonly isFocusRoute = computed(() => this.isLiveMatchRoute() || /^\/app\/trainings\/[^/]+\/play(?:[?#].*)?$/.test(this.currentUrl()));
  // Community is the aggregator for the whole communities/trainings journey, so it stays lit across all of it.
  readonly communityActive = computed(() => /^\/app\/(community|communities|trainings)\b/.test(this.currentUrl()));
  readonly notificationCount = computed(() => this.state.notifications().length);

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        // Navigating from inside the More sheet should always dismiss it.
        this.moreOpen.set(false);
      });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.moreOpen.set(false);
  }

  openMore(): void {
    this.haptics.selection();
    this.moreOpen.set(true);
  }

  closeMore(): void {
    this.moreOpen.set(false);
  }

  toggleNotifications(): void {
    // Collapsed desktop rail can't show the inline list — expand the rail first.
    if (this.collapsed()) {
      this.collapsed.set(false);
    }
    this.notifOpen.set(!this.notifOpen());
  }

  initials(): string {
    const name = this.state.currentUser()?.displayName ?? 'User';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'U';
  }
}
